import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MeterType, MeterTypeReadingStep } from '../../../models/meter.model';
import { Observable } from 'rxjs';
import { TranslationUtils } from '../../../../assets/i18n/translation.utils';
import { ToastrService } from 'ngx-toastr';
import { MeterService } from '../../../services/meter.service';
import { SltStatus } from '../../../models/customer.model';
import { OptionObj } from 'shared-lib';
import { CustomerService } from '../../../services/customer.service';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-meter-type',
  templateUrl: './crud-meter-type.component.html',
  styleUrl: './crud-meter-type.component.scss',
  standalone: false
})
export class CrudMeterTypeComponent implements OnInit {
  utilityId: number = 0;
  searchText: string = '';
  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;
  meterTypes: MeterType[] = [];
  selectedMeterType: MeterType = new MeterType();
  updatedMeterType: MeterType = new MeterType();
  sltStatuses: SltStatus[] = [];
  selectedSltStatuses: SltStatus[] = [];
  updatedSltStatusIds: number[] = [];
  updatedImageUrl: string | undefined = '';
  updatedImageFile: File | undefined;
  updatedReadingSteps: MeterTypeReadingStep[] = [];
  isEditMode: boolean = false;
  isReadingStepsVisible: boolean = false;
  messages$: Observable<{ [key: string]: string }>;

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  @ViewChild('closeEditModal') closeEditModal: ElementRef;

  constructor(
    private toastr: ToastrService,
    private meterService: MeterService,
    private customerService: CustomerService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.getSltStatuses();
    this.reloadTable(1);
  }

  openAddModal() {
    this.isEditMode = false;
    this.selectedMeterType = new MeterType();
    this.updatedMeterType = new MeterType();
    this.updatedImageUrl = '';
    this.updatedImageFile = undefined;
    this.updatedReadingSteps = [];
    this.selectedSltStatuses = [];
    this.updatedSltStatusIds = [];
    this.sltStatuses = this.sltStatuses.map(slt => ({ ...slt, checked: false }));
    this.isReadingStepsVisible = false;
  }

  openEditModal() {
    this.isEditMode = true;
    this.fillInputFields();
  }

  private getFilterObject() {
    return {
      searchValue: this.searchText
    };
  }

  reloadTable(page: number | null = null) {
    if (page) {
      this.currentPage = page;
    }
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };
    this.getAllMeterTypes(dataIn);
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  private getAllMeterTypes(dataIn: any) {
    this.meterService.getAllMeterTypes(dataIn).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.meterTypes = responseData.data.data;
          this.meterTypes.forEach((x) => {
            x.currentSltStatuses = x.meterSltStatuses?.map((y) => y.name).join(', ');
          });
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]);
        });
      },
    });
  }

  getSltStatuses() {
    this.customerService.getSltStatusesForOptions().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltStatuses = responseData.data.map((item: OptionObj) => ({
            id: Number(item.key),
            value: item.value,
            checked: false,
            utilityId: 0,
            name: item.value,
            installationAndPowerSource: 0
          }));
        } else if (response?.status === 204) {
          this.sltStatuses = [];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  selectMeterType(meterType: MeterType) {
    this.selectedMeterType = meterType;
    this.openEditModal();
  }

  onDeleteMeterType(meterType: MeterType){
    this.selectedMeterType = meterType;
    this.isEditMode = false;
  }

  private fillInputFields() {
    this.updatedMeterType = { ...this.selectedMeterType };
    this.updatedImageUrl = this.selectedMeterType.imageUrl ?? '';
    this.updatedImageFile = undefined;
    this.updatedReadingSteps = this.selectedMeterType.readingSteps ? [...this.selectedMeterType.readingSteps] : [];
    this.updatedSltStatusIds = this.selectedMeterType.meterSltStatuses?.map(slt => slt.id) ?? [];
    this.selectedSltStatuses = this.sltStatuses.filter(slt => this.updatedSltStatusIds.includes(slt.id));
    this.sltStatuses = this.sltStatuses.map(slt => ({
      ...slt,
      checked: this.updatedSltStatusIds.includes(slt.id)
    }));
  }

  onSltStatusSelected(itemList: any) {
    this.updatedSltStatusIds = this.sltStatuses
      .filter(slt => itemList.includes(slt.name))
      .map(slt => slt.id);
    this.selectedSltStatuses = this.sltStatuses
      .filter(slt => this.updatedSltStatusIds.includes(slt.id));
  }

  save() {
    if (!this.updatedMeterType.name) {
      this.toastr.warning("Name can't be empty!");
      return;
    }
    if (this.selectedSltStatuses.length === 0) {
      this.toastr.warning("You have to select at least one SLT Status!");
      return;
    }
    if (this.isReadingStepsVisible && this.updatedReadingSteps.some(step => !step.description)) {
      this.toastr.warning("All reading steps must have a description!");
      return;
    }

    this.updatedMeterType.id = this.isEditMode ? this.selectedMeterType.id : 0;
    this.updatedMeterType.utilityId = this.utilityId;
    this.updatedMeterType.meterSltStatuses = this.selectedSltStatuses;
    this.updatedMeterType.imageFile = this.updatedImageFile;
    this.updatedMeterType.imageUrl = this.updatedImageUrl || '';
    this.updatedMeterType.readingSteps = this.updatedReadingSteps;

    this.meterService.saveMeterType(this.updatedMeterType).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(this.isEditMode ? "Meter Type successfully updated." : "Meter Type successfully added.");
          this.closeEditModal?.nativeElement?.click();
          setTimeout(() => {
            this.reloadTable(1);
          }, 500);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]);
        });
      },
    });
  }

  deleteMeterType() {
    this.meterService.deleteMeterType(this.selectedMeterType.id).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(responseData?.message);
          this.onPageChange(1);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  onFileSelected($event: any): void {
    const input = $event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (file.size > 2097152) {
        this.messages$.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_CHOOSE_SMALLER_IMAGE_KEY]);
        });
      } else {
        this.updatedImageFile = file;
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.updatedImageUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    }
  }

  addReadingSteps(event: Event, stepOrder: number) {
    event.preventDefault();
    event.stopPropagation();
    if (stepOrder === 1) {
      this.updatedReadingSteps = [];
      this.isReadingStepsVisible = true;
    } else if (
      !this.updatedReadingSteps.find((x) => x.stepOrder === stepOrder - 1)?.description
    ) {
      this.messages$.subscribe((messages) => {
        this.toastr.error(messages[TranslationUtils.WARNING_PREVIOUS_STEP_KEY]);
      });
      return;
    }
    const step = new MeterTypeReadingStep();
    step.stepOrder = stepOrder;
    this.updatedReadingSteps.push(step);
  }

  removeStep(event: Event, position: number) {
    event.preventDefault();
    event.stopPropagation();
    this.updatedReadingSteps = this.updatedReadingSteps.filter((x) => x.stepOrder !== position);
    this.updatedReadingSteps.forEach((element) => {
      element.stepOrder = element.stepOrder > position ? element.stepOrder - 1 : element.stepOrder;
    });
  }

  removeImage(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.updatedImageUrl = '';
    this.updatedImageFile = undefined;
  }

  onSearch() {
    this.currentPage = 1;
    this.reloadTable(1);
  }
}